import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { getJeepRallyMediaUrl } from "../../../../../api/jeep-rally/api";
import { useGetCategoryById } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  getCategoryApiErrorMessage,
  getCategoryTypeName,
  isJeepRallyAdmin,
  normalizeCategory,
} from "../utils/category.utils";

const DetailRow = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-muted small mb-1">{label}</div>
    <div>{value || "-"}</div>
  </div>
);

const formatCcRange = (category) => {
  const min = category?.minCC;
  const max = category?.maxCC;
  if (min == null && max == null) return "-";
  if (max == null || max === "") return `${min ?? 0}+`;
  return `${min ?? 0}–${max}`;
};

const displayRole = (role) => {
  if (role === "navigator") return "Codriver";
  if (role === "driver") return "Driver";
  if (role === "mechanic") return "Mechanic";
  return role || "";
};

const formatMemberRoles = (category) => {
  const members = Array.isArray(category?.members) ? category.members : [];
  if (members.length) {
    return members
      .map((member, index) => {
        if (typeof member === "string") {
          return `${index + 1}. ${displayRole(member)}`;
        }
        const role = member?.role || member?.name || member;
        return `${index + 1}. ${displayRole(role)}`;
      })
      .join(", ");
  }

  const roles = Array.isArray(category?.member_roles)
    ? category.member_roles
    : [];
  if (!roles.length) return "-";
  return roles
    .map((role, index) => `${index + 1}. ${displayRole(role)}`)
    .join(", ");
};

const CategoryViewPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const canManage = isJeepRallyAdmin();
  const locationCategory = history.location.state?.category || null;

  const {
    data: categoryData,
    isLoading,
    isError,
    error,
  } = useGetCategoryById(id);

  const category = normalizeCategory(categoryData) || locationCategory;
  const imageUrl = getJeepRallyMediaUrl(category?.image);
  const consentRaw = String(category?.consent || "").trim();
  const consentLooksLikeFile =
    /^https?:\/\//i.test(consentRaw) ||
    /^\/?uploads\//i.test(consentRaw) ||
    /\.(pdf|docx?|png|jpe?g|webp)(\?|#|$)/i.test(consentRaw);
  const consentUrl = consentLooksLikeFile
    ? getJeepRallyMediaUrl(consentRaw)
    : "";
  const consentHtml = !consentLooksLikeFile ? consentRaw : "";
  const isActive = category?.status !== "inactive";

  const goBack = () => history.push("/jeep-rally/categories");
  const goEdit = () => {
    if (!id) return;
    history.push(`/jeep-rally/categories/edit/${id}`, { category });
  };

  if (isLoading && !locationCategory) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((isError && !locationCategory) || !category) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getCategoryApiErrorMessage(
                error,
                "Category not found. Please open it from the Categories list."
              )}
            </Alert>
            <Button variant="outline-secondary" className="mt-3" onClick={goBack}>
              Back to categories
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to categories list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">View Category</h4>
          </div>
          {canManage ? (
            <Button variant="primary" size="sm" onClick={goEdit}>
              Edit
            </Button>
          ) : null}
        </div>

        <div className="card-body">
          <Card className="shadow-none mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div className="d-flex gap-3 align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center overflow-hidden border rounded bg-light"
                    style={{ width: 96, height: 72, flexShrink: 0 }}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={category.title || "Category"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span className="text-muted small">No image</span>
                    )}
                  </div>
                  <div>
                    <h5 className="mb-1">{category.title || "-"}</h5>
                    <div className="text-muted mb-2">
                      Category ID: <code>{id}</code>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg="secondary">{category.key || "-"}</Badge>
                      <Badge bg={isActive ? "success" : "secondary"}>
                        {category.status || "active"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <DetailRow
                    label="Type"
                    value={getCategoryTypeName(category) || "-"}
                  />
                  <DetailRow label="CC range" value={formatCcRange(category)} />
                  <DetailRow
                    label="Max members"
                    value={
                      category.max_members === 0 || category.max_members
                        ? String(category.max_members)
                        : "-"
                    }
                  />
                </div>
                <div className="col-md-6">
                  <DetailRow
                    label="Codriver allowed"
                    value={category.navigator_allowed ? "Yes" : "No"}
                  />
                  <DetailRow
                    label="Member roles"
                    value={formatMemberRoles(category)}
                  />
                  <DetailRow
                    label="Signed consent"
                    value={
                      category.isSigned || category.consent_document
                        ? "Yes"
                        : "No"
                    }
                  />
                </div>
              </div>

              <div className="mb-0">
                <div className="text-muted small mb-1">Description</div>
                <div>{category.description || "-"}</div>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-none mb-0">
            <Card.Body>
              <h5 className="mb-3">Consent template</h5>
              {consentHtml ? (
                <div
                  className="border rounded p-3 bg-light"
                  dangerouslySetInnerHTML={{ __html: consentHtml }}
                />
              ) : consentUrl ? (
                <a href={consentUrl} target="_blank" rel="noopener noreferrer">
                  Open consent template file
                </a>
              ) : (
                <p className="text-muted mb-0">No consent template set.</p>
              )}
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={goBack}>
              Back
            </Button>
            {canManage ? (
              <Button variant="primary" onClick={goEdit}>
                Edit Category
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryViewPage;
